import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Radio, Video } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import {
  getSpecialLinks,
  openSpecialLink,
  type SpecialLinkMeta,
  type SpecialLinkOpen,
} from '../lib/api';
import { useAuthStore } from '../lib/auth-store';

/**
 * Subscription-gated live links. The list carries NO URL — each open is a
 * server round-trip behind the subscription gate. Vimeo plays inline (domain-
 * locked embed on Vimeo's side is the real anti-share control) with a per-user
 * watermark; Zoom/other open in a new tab.
 */
export function SpecialLinksPage() {
  const q = useQuery({ queryKey: ['special-links'], queryFn: getSpecialLinks });
  const [player, setPlayer] = useState<{ title: string; embedUrl: string } | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function open(link: SpecialLinkMeta) {
    setError(null);
    setOpening(link.id);
    try {
      const res: SpecialLinkOpen = await openSpecialLink(link.id);
      if (res.kind === 'vimeo_embed') {
        setPlayer({ title: res.title, embedUrl: res.embedUrl });
      } else {
        window.open(res.url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      setError('Could not open this link. Your subscription may have lapsed.');
    } finally {
      setOpening(null);
    }
  }

  const links = q.data ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h1 flex items-center gap-2">
          <Radio className="h-6 w-6 text-primary" /> Special links
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Live Zoom / Vimeo sessions for subscribed members. Please don't share these links.
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {player && (
        <VimeoPlayer
          title={player.title}
          embedUrl={player.embedUrl}
          onClose={() => setPlayer(null)}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Available now</CardTitle>
        </CardHeader>
        <CardContent>
          {q.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : links.length === 0 ? (
            <p className="text-sm text-muted-foreground">No special links published yet.</p>
          ) : (
            <ul className="divide-y">
              {links.map((l) => (
                <li key={l.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {l.type === 'vimeo' ? (
                      <Video className="h-4 w-4 text-muted-foreground shrink-0" />
                    ) : (
                      <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                    <span className="font-medium truncate">{l.title}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase">
                      {l.type}
                    </span>
                  </div>
                  <Button size="sm" disabled={opening === l.id} onClick={() => open(l)}>
                    {opening === l.id ? 'Opening…' : l.type === 'vimeo' ? 'Watch' : 'Join'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function VimeoPlayer({
  title,
  embedUrl,
  onClose,
}: {
  title: string;
  embedUrl: string;
  onClose: () => void;
}) {
  const user = useAuthStore((s) => s.user);
  const mark = user?.email ?? user?.fullName ?? 'member';
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">{title}</CardTitle>
        <Button variant="outline" size="sm" onClick={onClose}>
          Close
        </Button>
      </CardHeader>
      <CardContent>
        <div className="relative aspect-video w-full overflow-hidden rounded-md bg-black">
          <iframe
            src={embedUrl}
            title={title}
            className="absolute inset-0 h-full w-full"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
          {/* Per-user watermark — deters screen-record-and-reshare (traceable to a person). */}
          <div className="pointer-events-none absolute inset-0 flex items-end justify-end p-3">
            <span className="rounded bg-black/30 px-2 py-0.5 text-[10px] text-white/70 select-none">
              {mark}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
